import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, Eye, FileCode2, FileMinus2, FileText, Printer } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { DialogoDeNotaDeCredito } from './DialogoDeNotaDeCredito';
import { PanelDeLaFactura } from './PanelDeLaFactura';
import { useComprobante } from './useComprobante';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { ESTADOS, SITUACIONES, colorDelEstado, colorDeLaSituacion } from './estadosDeFactura';
import type { FacturaListada } from './estadosDeFactura';

export function PaginaFacturas() {
  const [buscar, setBuscar] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [estado, setEstado] = useState('');
  const [situacionSri, setSituacionSri] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abierta, setAbierta] = useState<FacturaListada | null>(null);
  const [acreditando, setAcreditando] = useState<FacturaListada | null>(null);
  const comprobante = useComprobante();
  const { puede } = useSesion();

  const facturas = useQuery({
    queryKey: ['facturas', buscar, periodo, estado, situacionSri, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<FacturaListada>>('/facturas', {
        query: { buscar, periodo, estado, situacionSri, pagina, tamanio },
      }),
  });

  const columnas: Columna<FacturaListada>[] = [
    {
      clave: 'numero',
      encabezado: 'Comprobante',
      contenido: (f) => (
        <span className="block min-w-0">
          {f.numero ? (
            <span className="importe block truncate text-xs font-semibold text-armazon-800">
              {f.numero}
            </span>
          ) : (
            <span className="block text-[11px] uppercase text-armazon-400">Sin numerar</span>
          )}
          <span className="flex items-center gap-1.5 text-[10px] uppercase text-armazon-400">
            {f.periodo}
            {f.historica && (
              <span
                title="Viene del sistema anterior"
                className="inline-flex items-center gap-1 rounded bg-armazon-100 px-1 text-armazon-500"
              >
                <Archive className="size-2.5" aria-hidden />
                Histórica
              </span>
            )}
          </span>
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (f) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-700">
            {f.cliente}
          </span>
          <span className="importe block text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'emitida',
      encabezado: 'Emitida',
      contenido: (f) => (
        <span className="importe text-xs text-armazon-600">{enFecha(f.fechaEmision)}</span>
      ),
    },
    {
      clave: 'vence',
      encabezado: 'Vence',
      contenido: (f) => (
        <span className="importe text-xs text-armazon-400">{enFecha(f.vencimiento)}</span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => (
        <span className="block">
          <span className="importe text-xs font-bold text-armazon-900">${f.total.toFixed(2)}</span>
          {f.descuento > 0 && (
            <span
              className="importe block text-[10px] text-marca-700"
              title="Descuento por exoneración"
            >
              −${f.descuento.toFixed(2)}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'estado',

      encabezado: 'Cobro',
      alinear: 'centro',
      contenido: (f) => (
        <span
          title="Si está pagada. Es independiente de lo que sepa el SRI."
          className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${colorDelEstado(f.estado)}`}
        >
          {f.estado}
        </span>
      ),
    },
    {
      clave: 'sri',
      encabezado: 'Ante el SRI',
      alinear: 'centro',
      contenido: (f) => (
        <span className="block">
          <span
            className={`text-[10px] font-bold uppercase ${colorDeLaSituacion(f.situacionSri)}`}
            title={
              f.claveAcceso
                ? `Qué sabe el SRI del comprobante · ${f.claveAcceso}`
                : 'Qué sabe el SRI del comprobante'
            }
          >
            {f.situacionSri.replace(/_/g, ' ')}
          </span>

          {f.acreditado > 0 && (
            <span className="mt-0.5 block text-[10px] uppercase text-amber-700">
              {f.tieneNotaTotal ? 'Nota de crédito' : `Acreditado $${f.acreditado.toFixed(2)}`}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-28',
      contenido: (f) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila icono={Eye} titulo="Ver la factura" alPulsar={() => setAbierta(f)} />

          {tieneComprobante(f) && (
            <>
              <BotonDeFila
                icono={Printer}
                titulo="Abrir el RIDE"
                alPulsar={() => void comprobante.abrirRide(f.id, f.historica)}
                ocupado={comprobante.ocupado === f.id}
              />
              <BotonDeFila
                icono={FileCode2}
                titulo="Descargar el XML autorizado"
                alPulsar={() => void comprobante.descargarXml(f.id, f.numero, f.historica)}
                ocupado={comprobante.ocupado === f.id}
              />

              {puede('facturas.anular') && !f.historica && f.situacionSri === 'autorizada' && (
                <BotonDeFila
                  icono={FileMinus2}
                  titulo={
                    f.tieneNotaTotal
                      ? 'Ya tiene una nota de crédito total'
                      : f.acreditado > 0
                        ? `Acreditar otra parte · ya lleva $${f.acreditado.toFixed(2)}`
                        : 'Acreditar con una nota de crédito'
                  }
                  alPulsar={() => setAcreditando(f)}
                  ocupado={f.tieneNotaTotal}
                />
              )}
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={FileText}
        color="azul"
        titulo="Facturas"
        subtitulo="Todo lo emitido, con lo que el SRI sabe de cada comprobante"
      />

      <Tabla
        datos={facturas.data}
        cargando={facturas.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="facturas"
        iconoVacio={FileText}
        mensajeVacio="Ninguna factura coincide"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(v) => {
                setBuscar(v);
                setPagina(0);
              }}
              marcador="Número, clave de acceso, cédula o nombre…"
            />

            <input
              type="month"
              value={periodo}
              onChange={(evento) => {
                setPeriodo(evento.target.value);
                setPagina(0);
              }}
              aria-label="Período"
              className="importe h-9 rounded-lg border border-armazon-200 px-2 text-xs outline-none transition-colors focus:border-marca-400"
            />

            <Filtro
              valor={estado}
              opciones={ESTADOS}
              vacio="Todos los estados"
              alCambiar={(v) => {
                setEstado(v);
                setPagina(0);
              }}
            />

            <Filtro
              valor={situacionSri}
              opciones={SITUACIONES}
              vacio="Ante el SRI: todo"
              alCambiar={(v) => {
                setSituacionSri(v);
                setPagina(0);
              }}
            />
          </span>
        }
      />

      <VistaPreviaDeImpresion
        abierto={comprobante.ride !== null || comprobante.ocupado !== null}
        alCerrar={comprobante.cerrar}
        titulo="RIDE"
        subtitulo="Representación impresa del comprobante electrónico"
        fichero={comprobante.ride}
        cargando={comprobante.ocupado !== null}
        formato="hoja"
        textoDeCarga="Preparando el RIDE…"
      />

      {abierta && <PanelDeLaFactura factura={abierta} abierto alCerrar={() => setAbierta(null)} />}

      <DialogoDeNotaDeCredito
        abierto={acreditando !== null}
        factura={acreditando}
        alCerrar={() => setAcreditando(null)}
        alEmitir={() => void facturas.refetch()}
      />
    </>
  );
}

function Filtro({
  valor,
  opciones,
  vacio,
  alCambiar,
}: {
  valor: string;
  opciones: readonly { valor: string; etiqueta: string }[];
  vacio: string;
  alCambiar: (valor: string) => void;
}) {
  return (
    <select
      value={valor}
      onChange={(evento) => alCambiar(evento.target.value)}
      aria-label={vacio}
      className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none transition-colors focus:border-marca-400"
    >
      <option value="">{vacio}</option>
      {opciones.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.etiqueta}
        </option>
      ))}
    </select>
  );
}

function tieneComprobante(f: FacturaListada): boolean {
  return f.historica ? f.situacionSri === 'autorizada' : f.situacionSri !== 'interna';
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
